import { db } from '@/src/renderer/lib/db'
import { getPlatformAPI } from '@/src/renderer/lib/platform'
import { loginUser, requestPasswordReset, sendEmailOTP, syncUserLanguage, updatePassword, updateUserProfile, verifyEmailOTP } from '@/src/renderer/lib/supabase/auth'
import { updateUserAvatarPreset } from '@/src/renderer/lib/supabase/avatars'
import { rememberDisplayName } from '@/src/renderer/lib/profile/rememberDisplayName'
import { getCurrentUserId, supabase } from '@/src/renderer/lib/supabase/client'
import { getSyncEntitlement, saveUserIntentions, setSyncEntitlement } from '@/src/renderer/lib/supabase/queries'
import { cloudTableOf, findOwnCloudRowsByLocalIds } from '@/src/renderer/lib/supabase/queries/localIdLookup'
import { getSetupFlowProgress, startSetupFlow, updateSetupFlowProgress } from '@/src/renderer/lib/supabase/setupFlow'
import { getLastSyncedAt } from '@/src/renderer/lib/sync/lastSynced'

const OFFLINE_PROFILE_ID = 'offline-user'
import type { AccountSource } from './accountSource'

/** The app's account source: Supabase for the account, the main process for storage mode. Behaves as the pages did before phase 8f. */
export const supabaseAccountSource: AccountSource = {
  getUserId() {
    return getCurrentUserId()
  },

  getSyncEntitlement(userId) {
    return getSyncEntitlement(userId)
  },

  setSyncEntitlement(userId, enabled) {
    return setSyncEntitlement(userId, enabled)
  },

  getLastSyncedAt() {
    return getLastSyncedAt()
  },

  setStorageMode(mode) {
    return getPlatformAPI().setStorageMode(mode)
  },

  // Der Main-Prozess schickt `storage-mode-changed` an jedes Fenster; im Web gibt es kein `window.api`.
  onSyncSettingsChanged(listener) {
    return window.api?.receive?.('storage-mode-changed', listener) ?? (() => {})
  },

  // Das Einstellungsfenster bittet den Main-Prozess, das Hauptfenster zu holen und dort anzumelden (`useConnectCloud`).
  connectCloud() {
    void window.api?.invoke?.('connect-happycloud')
  },

  async findCloudRowsByLocalIds(table, localIds) {
    const userId = await getCurrentUserId()
    if (!userId) return new Map()
    return findOwnCloudRowsByLocalIds(cloudTableOf(table), localIds, userId)
  },

  async getCloudUser() {
    if (!supabase) return null
    const { data: { user } } = await supabase.auth.getUser()
    return user
      ? {
          id: user.id,
          fullName: user.user_metadata?.full_name ?? null,
          // Supabase legt `name` beim Anlegen aus dem Anbieter an und rührt es
          // danach nicht mehr an; `full_name` überschreibt `updateUserProfile`.
          providerName: (user.user_metadata?.name as string | undefined) ?? null,
        }
      : null
  },

  sendEmailCode(email, language) {
    return sendEmailOTP(email, language)
  },

  async verifyEmailCode(email, code) {
    const result = await verifyEmailOTP(email, code)
    return !!(result.success && result.user && result.session)
  },

  async loginWithPassword(email, password) {
    const result = await loginUser({ email, password })
    return !!(result.success && result.user && result.session)
  },

  syncLanguage(language) {
    return syncUserLanguage(language)
  },

  async refreshSession() {
    if (supabase) await supabase.auth.refreshSession()
  },

  requestPasswordReset(email, language) {
    return requestPasswordReset(email, language)
  },

  updatePassword(newPassword) {
    return updatePassword(newPassword)
  },

  onPasswordRecovery(listener) {
    if (!supabase) return () => {}
    const { data: { subscription } } = supabase.auth.onAuthStateChange(event => {
      if (event === 'PASSWORD_RECOVERY') listener()
    })
    return () => subscription.unsubscribe()
  },

  async completeOAuth({ accessToken, refreshToken }, language) {
    const { data, error } = await supabase!.auth.setSession({ access_token: accessToken, refresh_token: refreshToken })
    if (error) return { status: 'error', message: error.message }
    // is_active gate — OAuth bypasses the Password-Verification Hook, so the flag is checked
    // client-side right after the session is set (AuthProvider checks it again).
    if (data.user?.id) {
      const { data: profile } = await supabase!.from('profiles').select('is_active').eq('id', data.user.id).maybeSingle()
      if (profile?.is_active === false) {
        await supabase!.auth.signOut()
        return { status: 'inactive' }
      }
    }
    await syncUserLanguage(language)
    // Den Anzeigenamen mitnehmen: Die Anmeldeseite begrüßt damit beim nächsten
    // Mal, und ohne diese Zeile blieb im Browser der Stand von vorher stehen.
    rememberDisplayName(data.user?.user_metadata?.full_name)
    return { status: 'ok' }
  },

  saveProfile(changes) {
    return updateUserProfile(changes)
  },

  async saveAvatarPreset(userId, avatarPresetId) {
    await updateUserAvatarPreset(userId, avatarPresetId)
  },

  getOfflineProfile() {
    return db.userProfiles.get(OFFLINE_PROFILE_ID)
  },

  async putOfflineProfile(profile) {
    await db.userProfiles.put(profile)
  },

  async updateOfflineProfile(changes) {
    const existing = await db.userProfiles.get(OFFLINE_PROFILE_ID)
    if (!existing) return false
    await db.userProfiles.update(OFFLINE_PROFILE_ID, changes)
    return true
  },

  async getLocalUserName() {
    const profiles = await db.userProfiles.orderBy('lastModified').reverse().toArray()
    return profiles.find(p => p.fullName && p.fullName.trim().length > 0)?.fullName ?? null
  },

  startSetup() {
    return startSetupFlow()
  },

  getSetupProgress() {
    return getSetupFlowProgress()
  },

  async updateSetupProgress(step, slideReached, slideViewed) {
    // Nur mitgeben, was gesetzt ist — genau wie die Seiten es vorher riefen.
    if (slideReached === undefined) await updateSetupFlowProgress(step)
    else await updateSetupFlowProgress(step, slideReached, slideViewed)
  },

  saveIntentions(userId, intentions) {
    return saveUserIntentions(userId, intentions)
  },

  // Nur Einwerfen, keine Rückgabe: Die Tabelle lässt `anon` nicht lesen, ein
  // `.select()` hier scheiterte an der RLS.
  async submitAnonymousOnboarding(row) {
    if (!supabase) return false
    try {
      const { error } = await supabase.from('onboarding_responses').insert(row)
      return !error
    } catch {
      return false
    }
  },
}
